import { createStore } from 'vuex'
import {
  activeLayout,
  defaultPage,
  isLoggedIn,
  updateSession,
  logout,
  setActiveSite,
  getSearchQuery,
  setSearchQuery,
  registerUserActivity,
  setSessionLength,
  updateSessionTimeout,
  clearSessionTimeout,
  hasPermissions,
  getInstitutionDeathClaims,
  setInstitutionDeathClaims,
  isProfileUpToDate,
  setIsProfileUpToDate,
} from './vuex-actions'
import { STAFF, MANAGER, OSR, CLIENT, TEAM } from './roles'
import { CLIENT_LAYOUT, OSR_LAYOUT, ADMIN, DEFAULT_CLIENT_PAGE, DEFAULT_ADMIN_PAGE } from './layouts'
import Axios from './axios'
import VuexPersist from 'vuex-persist'
import { confirmRefreshSession } from './components/dialogs'
import { useGlobalUtils } from './helpers/globalUtils'
import moment from 'moment-timezone'

const { errorToast } = useGlobalUtils()

const vuexPersist = new VuexPersist({
  key: 'bcc-dashboard',
  storage: localStorage,
})

let defaultMsLength = 20 * 60 * 1000
let msBuffer = 1 * 60 * 1000

const SESSION_LENGTH = 15 * 60 * 1000 // 15 minutes
const WARNING_BUFFER = 1 * 60 * 1000 // 1 minute warning
const ACTIVITY_CHECK_INTERVAL = 60 * 1000 // Check every minute
const INACTIVITY_THRESHOLD = 10 * 60 * 1000 // 10 minutes of inactivity before showing prompt

export async function logOut() {
  store.state.isLoading = true
  console.info('Logging out...')

  await Axios.post(`/session/logout`)

  store.commit(logout)

  window.history.replaceState({}, '', '#/login')
  location.reload()
}

async function launchSessionExpiredPrompt() {
  if (localStorage.getItem('annualQuestionnaireIsOpen') === 'true') {
    console.info('Annual Questionnaire is open, skipping session prompt...')
    store.state.attemptedSessionRefresh += 1
    return
  }

  if (!store.getters.isLoggedIn) {
    return
  }

  // Disable select portions of the screen
  let menuControl = document.querySelector('.menu-control-top')
  let collapseControl = document.querySelector('.menu-control-bottom')

  if (menuControl) {
    menuControl.style.display = 'none'
  }

  if (collapseControl) {
    collapseControl.style.display = 'none'
  }

  let secondsLeft = Math.round(msBuffer / 1000)

  const onConfirm = async function () {
    store.state.isLoading = true

    try {
      const req = await Axios.get(`/session/refresh`)

      if (req && req.status !== 200) {
        throw new Error('Failed to refresh session')
      }
    } catch (e) {
      errorToast('Failed to refresh session, logging out.')
      await logOut()
    } finally {
      if (menuControl) {
        menuControl.style.display = 'flex'
      }

      if (collapseControl) {
        collapseControl.style.display = 'flex'
      }

      store.state.isLoading = false
    }
  }

  const onCancel = async function () {
    await logOut()

    if (menuControl) {
      menuControl.style.display = 'flex'
    }

    if (collapseControl) {
      collapseControl.style.display = 'flex'
    }
  }

  const userChoice = await confirmRefreshSession(secondsLeft)

  userChoice === true ? await onConfirm() : await onCancel()
}

function getMsBuffer(msLength) {
  let buf = msBuffer
  if (buf >= msLength) {
    buf = 30 * 1000
  }
  return buf
}

async function checkActivityAndRefreshSession() {
  if (!store.getters.isLoggedIn || !store.state.lastUserActivity) {
    return
  }

  const now = moment().utc()
  const lastActivity = moment(store.state.lastUserActivity)
  const inactiveTime = now.diff(lastActivity)

  // If user has been inactive for more than 10 minutes, show the session expired prompt
  if (inactiveTime >= INACTIVITY_THRESHOLD) {
    console.info('User inactive for 10+ minutes, launching session expired prompt...')
    await launchSessionExpiredPrompt()
    return
  }

  // If user has been active within the session length, refresh session
  if (inactiveTime < SESSION_LENGTH - WARNING_BUFFER) {
    try {
      console.info('User active, refreshing session...')
      const response = await Axios.get('/session/refresh')

      if (response && response.status !== 200) {
        throw new Error('Failed to refresh session')
      }

      store.commit(updateSessionTimeout)
    } catch (error) {
      console.error('Failed to refresh session:', error)
      await launchSessionExpiredPrompt()
    }
  }
}

export const store = createStore({
  plugins: [vuexPersist.plugin],
  state: {
    isLoading: false,
    attemptedSessionRefresh: 0,
    timeoutPrompt: null,
    timeout: null,
    searchQuery: '',
    clientSession: {
      siteId: 0,
      userId: 0,
      userRole: CLIENT,
      isMFAEnabled: false,
      isMFAValid: false,
      institutionId: 0,
      msLength: defaultMsLength,
    },
    activeSite: {
      id: 0,
      name: '',
      displayName: '',
      url: '',
      logoUrl: '',
      linkedUrl: '',
    },
    lastUserActivity: null,
    activityCheckInterval: null,
    institutionDeathClaims: [],
    hasNewAccountRequests: false,
  },
  getters: {
    [hasPermissions]: ({ state }, permissions = []) => {
      return state.clientSession && state.clientSession.permissions.filter((permission) => permissions.includes(permission)).length > 1
    },
    [getSearchQuery]: (state) => {
      return state.searchQuery
    },
    [getInstitutionDeathClaims]: (state) => {
      return state.institutionDeathClaims
    },
    [activeLayout]: (state) => {
      if (!state.clientSession) {
        return CLIENT_LAYOUT
      }

      if (state.clientSession && [STAFF, MANAGER].includes(state.clientSession.userRole)) {
        return ADMIN
      }
      if (state.clientSession.userRole === OSR || state.clientSession.userRole === TEAM) {
        return OSR_LAYOUT
      }
      if (state.clientSession.userRole === CLIENT) {
        return CLIENT_LAYOUT
      }
      return CLIENT_LAYOUT // default
    },
    [isLoggedIn]: (state) => {
      return state.clientSession && state.clientSession.userId > 0
    },
    [defaultPage]: (state) => {
      if (state.clientSession && [STAFF, MANAGER].includes(state.clientSession.userRole)) {
        return DEFAULT_ADMIN_PAGE
      }
      return DEFAULT_CLIENT_PAGE // default
    },
    [isProfileUpToDate]: (state) => {
      return state.clientSession && state.clientSession.isProfileUpToDate
    },
  },
  mutations: {
    [setIsProfileUpToDate](state, isProfileUpToDate) {
      if (state.clientSession) {
        state.clientSession.isProfileUpToDate = isProfileUpToDate
      }
    },
    [setSessionLength](state, msLength) {
      if (msLength <= msBuffer) {
        msBuffer = getMsBuffer(msLength)
      }
      state.msLength = msLength || defaultMsLength
    },
    [setSearchQuery](state, searchQuery) {
      state.searchQuery = searchQuery
    },
    [setInstitutionDeathClaims](state, deathClaims) {
      state.institutionDeathClaims = deathClaims
    },
    [updateSessionTimeout](state) {
      console.info('Updating session timeout...')

      if (!state.clientSession) {
        return
      }

      // Clear existing timeouts
      if (state.timeoutPrompt) {
        clearTimeout(state.timeoutPrompt)
      }
      if (state.timeout) {
        clearTimeout(state.timeout)
      }
      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
      }

      // Set new session length
      state.clientSession.msLength = SESSION_LENGTH

      // Start activity checking
      state.activityCheckInterval = setInterval(checkActivityAndRefreshSession, ACTIVITY_CHECK_INTERVAL)
    },
    [clearSessionTimeout](state) {
      clearTimeout(state.timeoutPrompt)
      state.timeoutPrompt = null
      clearTimeout(state.timeout)
      state.timeout = null

      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
        state.activityCheckInterval = null
      }
    },
    [updateSession](state, session) {
      console.info('Updating session...')

      if (state.timeoutPrompt && state.timeoutPrompt !== null) {
        clearTimeout(state.timeoutPrompt)
      }
      if (state.timeout && state.timeout !== null) {
        clearTimeout(state.timeout)
      }

      if (state.clientSession) {
        state.clientSession = session
        if (state.clientSession.msLength <= msBuffer) {
          msBuffer = getMsBuffer(state.clientSession.msLength)
        }
        let timeout = state.clientSession.msLength - msBuffer
        //  state.timeoutPrompt = setTimeout(launchSessionExpiredPrompt, timeout)
        //state.timeout = setTimeout(logOut, state.clientSession.msLength)
      }
    },
    [registerUserActivity](state) {
      state.lastUserActivity = moment().utc()
      console.debug('User activity registered at:', state.lastUserActivity.format())
    },
    [logout](state) {
      // default session again
      clearTimeout(state.timeoutPrompt)
      state.timeoutPrompt = null
      clearTimeout(state.timeout)
      state.timeout = null

      if (state.clientSession) {
        state.clientSession = {
          siteId: 0,
          userId: 0,
          userRole: CLIENT,
          institutionId: 0,
          msLength: 30 * 1000,
        }
      }

      // Clear activity interval
      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
        state.activityCheckInterval = null
      }
    },
    [setActiveSite](state, site) {
      state.activeSite = site
    },
  },
  actions: {
    async updateActiveSite(context) {
      try {
        let route = `/site`
        let response = await Axios.get(route)
        context.commit(setActiveSite, response.data)
      } catch (error) {
      } finally {
      }
    },
  },
})
